import analytics, { getTrackedProductsValue, shouldTrackMarketingEvent } from '.';
import { getTenancy } from '../../hooks/useTenancy';
import { hasTargetingConsent } from '../consent';
import { typedWindow } from '../Window';
import {
  ECOM_EVENT_ORDER_COMPLETED,
  ECOM_EVENT_PRODUCT_ADDED,
  SegmentProduct,
  SegmentPurchaseEventProperties,
} from './events';

/**
 * Agentio Pixel integration for CTC attribution tracking.
 * Tracks: Product Added to Cart, Email Capture at Checkout, and Purchase events.
 * Only fires in production when targeting consent (C0004) is granted.
 *
 * @see https://app.agentio.com/brand/settings/pixel
 */
const AGENTIO_PIXEL_ID = 'fi_pixel';

interface AgentioConversionDetail {
  conversionType: string;
  value?: number;
}

declare global {
  interface Window {
    initAgentioPixel?: (pixelId: string) => void;
    agentioConversionHandler?: (detail: { detail: AgentioConversionDetail }) => void;
  }
}

function shouldFireAgentioEvent(): boolean {
  const tenancy = getTenancy();
  return (
    tenancy !== 'test' &&
    process.env.REACT_APP_ENVIRONMENT === 'production' &&
    hasTargetingConsent() &&
    typeof typedWindow.agentioConversionHandler === 'function'
  );
}

function fireAgentioConversion(conversionType: string, value?: number) {
  if (!shouldFireAgentioEvent()) {
    return;
  }

  const detail: AgentioConversionDetail = { conversionType };
  if (value !== undefined && value > 0) {
    detail.value = value;
  }

  typedWindow.agentioConversionHandler?.({ detail });
}

function agentioProductAdded(_messageId: string, properties: SegmentProduct) {
  // Calculate value including subscription for marketing attribution
  const subscriptionValue = properties.analyticsSubscriptionValue ?? properties.subscriptionPrice ?? 0;
  const totalValue = Number(properties.price) + Number(subscriptionValue);

  fireAgentioConversion('product_added_to_cart', totalValue);
}

function agentioOrderCompleted(_messageId: string, properties: SegmentPurchaseEventProperties) {
  const value = getTrackedProductsValue(properties);
  fireAgentioConversion('purchase', value);
}

// Map Segment events to Agentio handlers
type AgentioEventHandler = (messageId: string, properties: any) => void;
const AGENTIO_EVENT_MAP: Record<string, AgentioEventHandler> = {
  [ECOM_EVENT_PRODUCT_ADDED]: agentioProductAdded,
  [ECOM_EVENT_ORDER_COMPLETED]: agentioOrderCompleted,
};

function onTrack(event: string, properties: any, messageId: string) {
  const handler = AGENTIO_EVENT_MAP[event];
  if (!handler || !shouldTrackMarketingEvent(properties)) {
    return;
  }
  handler(messageId, properties);
}

/**
 * Track when user enters email at checkout.
 * Call this from checkout forms when email field is completed.
 */
export function trackAgentioEmailCapture() {
  fireAgentioConversion('user_entered_email_at_checkout');
}

/**
 * Set up Agentio pixel callbacks to listen for Segment events.
 * Should be called after analytics is ready and consent is granted.
 */
export function agentioSetupCallbacks() {
  analytics.onTrack(onTrack);
}

/**
 * Initialize the Agentio pixel. Called after the script loads and consent is granted.
 */
export function agentioInit() {
  if (
    getTenancy() !== 'test' &&
    process.env.REACT_APP_ENVIRONMENT === 'production' &&
    hasTargetingConsent() &&
    typeof typedWindow.initAgentioPixel === 'function'
  ) {
    typedWindow.initAgentioPixel(AGENTIO_PIXEL_ID);
  }
}
